import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ProceduresService } from './procedures.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ProceduresFormComponent,
  ProceduresFormData,
  ProceduresFormResult,
} from './procedures-form/procedures-form.component';
import { TdoTableActionsEnum } from '../../../tdo-controls/common/tdo-table/models/tdo-table.model';
import { MatTableDataSource } from '@angular/material/table';
import { TdoTableEvent } from '../../../tdo-controls/common/tdo-table/models/tdo-table.event';
import { ITdoTableItemModel } from '../../../tdo-controls/common/tdo-table/interfaces/i-tdo-table-item.model';
import { TdoTableBaseItemModel } from '../../../tdo-controls/common/tdo-table/models/tdo-table-base-item.model';
import { StaticDataService } from '../../../services/static-data.service';
import { EnumValidValueItemModel } from '../../../models/enum/enum-valid-value-item.model';
import { DialogService } from '../../../services/dialog.service';
import { TdoTableV2Component } from '../../../tdo-controls/common/tdo-table-v2/tdo-table-v2.component';
import { TdoTableSelectionEvent } from '../../../tdo-controls/common/tdo-table-v2/models/tdo-table-selection.event';
import { Globals } from '../../../globals';
import { CaseProcedureConfigurationModel } from '../../../models/caseProcedures/case-procedure-configuration.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TdoTableRowModel } from '../../../tdo-controls/common/tdo-table-v2/models/tdo-table-row.model';
import {
  CaseProcedureAdaCodeConfigurationModel,
} from '../../../models/caseProcedures/case-procedure-ada-code-configuration.model';
import { ConfigurationService } from '../configuration.service';
import {
  ProcedureFormMismatchDialogComponent,
} from '../../../dialog/procedure-form-mismatch-dialog/procedure-form-mismatch-dialog.component';

interface Form {
  id: string;
  dFormID: string;
  name: string;
  filledBy: string;
  isOpenByDefault: boolean;
  isToothRequired: boolean;
  isAddByDefault: boolean;
  formOrder: number;
  FormExpireIn: number;
  enumFormExpireTimeID: string;
}

interface Procedure {
  TDOCID: number;
  id: string;
  code: string;
  description: string;
  name: string;
  forms: Form[];
  adaCodes: CaseProcedureAdaCodeConfigurationModel[];
  deletedForms: Form[];
  enumMedicalSpecialtyID: string;
  partOfAppointmentGroup: boolean;
  appointmentAddByDefault: boolean;
  partOfVisitGroup: boolean;
  visitAddByDefault: boolean;
  enumProcedureCategoryID: string;
  enumProcedureActionCategoryID: string;
  enumProcedureTypeID: string;
  ParentCaseProcedureId: string;
  order: number;
  childProcedures: Procedure[];
  isExpanded: boolean;
  isDoNotDeploy: boolean;
}

interface FilledByEnum {
  id: string;
  name: string;
}

interface Specialties {
  id: string;
  description: string;
}

class ProcedureTableViewData extends TdoTableBaseItemModel implements ITdoTableItemModel {
  name: string;
  actions = [TdoTableActionsEnum.DELETE];
  model: Procedure;

  getActionTooltip(actionName: string): string {
    return 'Delete procedure';
  }
}

@Component({
  selector: 'app-procedures',
  templateUrl: './procedures.component.html',
  styleUrls: ['./procedures.component.scss'],
})
export class ProceduresComponent implements OnInit, AfterViewInit {
  @ViewChild('procedureTable') procedureTable: TdoTableV2Component;
  @ViewChild('formsTable') formsTable: TdoTableV2Component;

  isShiftPressed = false;
  codeInput = '';
  procedures: Procedure[] = [];
  selectedProcedure: Procedure = null;
  specialities: Specialties[] = [];
  selectedFormID = '';
  isCreating = false;
  isLoading = true;

  childProcSelected = false;

  sortPredicate: (data: any, sortHeaderId: string) => string;
  searchPredicate: (data: any, filter: string) => boolean;

  procedureFormsDataSource: MatTableDataSource<Form>;

  procedureActionCategoriesEnums: EnumValidValueItemModel[];
  procedureCategoriesEnums: EnumValidValueItemModel[];
  procedureMedicalSpecialtiesEnums: EnumValidValueItemModel[];
  procedureTypeEnums: EnumValidValueItemModel[];
  filledByList: EnumValidValueItemModel[] = [];

  @HostListener('document:keydown', ['$event']) onKeyDownHandler(
    event: KeyboardEvent
  ) {
    this.isShiftPressed = event.shiftKey;
  }

  @HostListener('document:keyup', ['$event']) onKeyUpHandler(
    event: KeyboardEvent
  ) {
    this.isShiftPressed = event.shiftKey;
  }

  constructor(
    private proceduresService: ProceduresService,
    private dialogService: DialogService,
    private globals: Globals,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public configurationService: ConfigurationService,
    private staticDataService: StaticDataService
  ) {
    this.searchPredicate = (data: TdoTableRowModel, filter: string) => {
      let triggered = false;

      if (data.model.name.toLowerCase().includes(filter)) {
        triggered = true;
      }

      const lowered = filter.toLowerCase();
      const codes = data.model?.adaCodes?.map((p) => p.Code?.toLowerCase());
      if (codes) {
        for (const code of codes) {
          if (code?.includes(lowered)) {
            triggered = true;
          }
        }
      }

      return triggered;
    };
  }

  async ngAfterViewInit() {
    if (this.staticDataService.isReady) {
      this.procedureMedicalSpecialtiesEnums = this.staticDataService.getValidValueItemsByValidValueName(
        'enumMedicalSpecialty'
      );
      this.procedureActionCategoriesEnums = this.staticDataService.getValidValueItemsByValidValueName(
        'enumProcedureActionCategory'
      );
      this.procedureCategoriesEnums = this.staticDataService.getValidValueItemsByValidValueName(
        'enumProcedureCategory'
      );
      this.procedureTypeEnums = this.staticDataService.getValidValueItemsByValidValueName(
        'enumProcedureType'
      );
      this.filledByList = this.staticDataService.getValidValueItemsByValidValueName(
        'enumFormFilledBy'
      );
      this.getData();
    } else {
      this.staticDataService.validValuesReadyEvent.subscribe({
        complete: () => {
          this.procedureMedicalSpecialtiesEnums = this.staticDataService.getValidValueItemsByValidValueName(
            'enumMedicalSpecialty'
          );
          this.procedureActionCategoriesEnums = this.staticDataService.getValidValueItemsByValidValueName(
            'enumProcedureActionCategory'
          );
          this.procedureCategoriesEnums = this.staticDataService.getValidValueItemsByValidValueName(
            'enumProcedureCategory'
          );
          this.procedureTypeEnums = this.staticDataService.getValidValueItemsByValidValueName(
            'enumProcedureType'
          );
          this.filledByList = this.staticDataService.getValidValueItemsByValidValueName(
            'enumFormFilledBy'
          );
          this.getData();
        },
      });
    }
  }

  async ngOnInit(): Promise<void> {
    // Intentionally left blank
  }

  async getData() {
    try {
      const result = await this.proceduresService.getApplicationProcedures().toPromise();
      this.procedures = this.createProceduresFromConfigurationModels(result);
      this.procedures = this.procedures.filter(
        (p) =>
          p.ParentCaseProcedureId === null ||
          p.ParentCaseProcedureId === '00000000-0000-0000-0000-000000000000'
      );
    } catch (e) {
      console.error('Error loading procedures:', e);
    }

    this.isLoading = false;
    this.procedures.sort((a, b) => (a.name > b.name ? 1 : -1));

    const timer = setInterval(() => {
      if (this.procedureTable) {
        this.procedureTable.updateData(this.procedures);
        this.selectedProcedure = this.procedureTable.dataSource[0];
        if (this.selectedProcedure) {
          this.getProcedureGroupInfo(this.selectedProcedure.id);
        }
        clearInterval(timer);
      }
    }, 250);
  }

  createProceduresFromConfigurationModels(
    models: CaseProcedureConfigurationModel[]
  ): Procedure[] {
    return models.map<Procedure>((o) => ({
      TDOCID: o.TDOCID,
      id: o.CaseProcedureId,
      code: o.Code,
      description: o.Description,
      name: o.ProcedureName,
      forms:
        o.cfgCaseProcedureForms.length !== 0
          ? o.cfgCaseProcedureForms.map<Form>((f) => ({
              id: f.CaseProcedureFormId,
              dFormID: (f as any).cfgDForm ? (f as any).cfgDForm.DFormId : '',
              name: (f as any).cfgDForm
                ? (f as any).cfgDForm.DFormName
                : 'No Form Name',
              filledBy: f.enumFormFilledByID,
              isOpenByDefault: f.isOpenByDefault,
              isAddByDefault: f.isAddByDefault,
              isRequireSignature: f.isRequireSignature,
              isSubmissionRequired: f.isSubmissionRequired,
              isToothRequired: f.isToothRequired,
              formOrder: f.formOrder,
              FormExpireIn: f.FormExpireIn,
              enumFormExpireTimeID: f.enumFormExpireTimeID,
            }))
          : [],
      adaCodes: o.cfgCaseProcedureADACodes,
      deletedForms: [],
      enumMedicalSpecialtyID: o.enumMedicalSpecialtyID,
      enumProcedureActionCategoryID: o.enumProcedureActionCategoryID,
      enumProcedureCategoryID: o.enumProcedureCategoryID,
      enumProcedureTypeID: o.enumProcedureTypeID,
      partOfAppointmentGroup: false,
      appointmentAddByDefault: false,
      partOfVisitGroup: false,
      visitAddByDefault: false,
      ParentCaseProcedureId: o.ParentCaseProcedureId ?? null,
      order: o.PresentOrder,
      childProcedures:
        this.createProceduresFromConfigurationModels(
          models.filter((p) => p.ParentCaseProcedureId === o.CaseProcedureId)
        ) || [],
      isExpanded: false,
      isDoNotDeploy: o.isDoNotDeploy,
    }));
  }

  getSpecialtyById(specialtyId) {
    return this.procedureMedicalSpecialtiesEnums.find(
      (p) => p.ValidValueItemId === specialtyId
    );
  }

  getCategoryByItemId(itemId: string) {
    if (this.procedureCategoriesEnums) {
      return this.procedureCategoriesEnums.find(
        (p) => p.ValidValueItemId === itemId
      );
    }
    return null;
  }

  getActionItemByItemId(itemId: string) {
    if (this.procedureActionCategoriesEnums) {
      return this.procedureActionCategoriesEnums.find(
        (p) => p.ValidValueItemId === itemId
      );
    }
    return null;
  }

  getTypeItemByItemId(itemId: string) {
    if (this.procedureTypeEnums) {
      return this.procedureTypeEnums.find(
        (p) => p.ValidValueItemId === itemId
      );
    }
    return null;
  }

  async getProcedureGroupInfo(procedureID: string): Promise<void> {
    try {
      const groupInfo = await this.proceduresService
        .getProcedureGroupInfo(procedureID)
        .toPromise();

      if (this.selectedProcedure) {
        this.selectedProcedure.partOfAppointmentGroup =
          groupInfo.PartOfAppointmentGroup;
        this.selectedProcedure.appointmentAddByDefault =
          groupInfo.AppointmentGroupAddedByDefault;
        this.selectedProcedure.partOfVisitGroup = groupInfo.PartOfVisitGroup;
        this.selectedProcedure.visitAddByDefault =
          groupInfo.VisitGroupAddedByDefault;
      }
    } catch (e) {
      this.showErrorMessage(e);
    }
  }

  private uuidv4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  create(): void {
    this.isCreating = true;

    const procedure: Procedure = {
      TDOCID: parseInt(this.globals.TDOCID, 10),
      id: null,
      code: '',
      description: '',
      name: '',
      forms: [],
      deletedForms: [],
      partOfAppointmentGroup: false,
      appointmentAddByDefault: true,
      partOfVisitGroup: false,
      visitAddByDefault: true,
      enumProcedureActionCategoryID: '',
      enumMedicalSpecialtyID: '',
      enumProcedureTypeID: '',
      enumProcedureCategoryID: '',
      ParentCaseProcedureId: null,
      adaCodes: [],
      order: 0,
      childProcedures: [],
      isExpanded: false,
      isDoNotDeploy: true,
    };

    this.selectedProcedure = procedure;
    this.selectedProcedure.forms = [];
    this.procedures.unshift(procedure);
    this.procedureTable.updateData(this.procedures);
    this.formsTable.matDataSource.data = this.selectedProcedure.forms;
    this.formsTable.updateData();
    setTimeout(() => {
      this.formsTable.matDataSource.data = this.selectedProcedure.forms;
      this.formsTable.updateData();
    }, 1);
  }

  createChildProcedure() {
    this.isCreating = true;

    const procedure: Procedure = {
      TDOCID: parseInt(this.globals.TDOCID, 10),
      id: null,
      code: '',
      description: '',
      name: '',
      forms: [],
      deletedForms: [],
      partOfAppointmentGroup: false,
      appointmentAddByDefault: true,
      partOfVisitGroup: false,
      visitAddByDefault: true,
      enumProcedureActionCategoryID: '',
      enumMedicalSpecialtyID: '',
      enumProcedureTypeID: '',
      enumProcedureCategoryID: '',
      ParentCaseProcedureId: this.selectedProcedure.id,
      order: 0,
      childProcedures: [],
      adaCodes: [],
      isExpanded: false,
      isDoNotDeploy: true,
    };

    if (!this.selectedProcedure.childProcedures) {
      this.selectedProcedure.childProcedures = [];
    }
    this.selectedProcedure.childProcedures.unshift(procedure);

    this.selectedProcedure = procedure;
    this.selectedProcedure.forms = [];
    this.formsTable.matDataSource.data = this.selectedProcedure.forms;
    this.formsTable.updateData();
    setTimeout(() => {
      this.formsTable.matDataSource.data = this.selectedProcedure.forms;
      this.formsTable.updateData();
    }, 1);
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.selectedProcedure = this.procedures[0];
  }

  async save(): Promise<void> {
    try {
      if (this.selectedProcedure.id === null) {
        this.selectedProcedure.TDOCID = parseInt(this.globals.TDOCID, 10);
        const result = await this.proceduresService
          .createProcedure(this.selectedProcedure)
          .toPromise();
        this.configurationService.setClean();
        const ret = [result].map<Procedure>((o) => ({
          TDOCID: o.TDOCID,
          id: o.CaseProcedureId,
          code: o.Code,
          description: o.Description,
          name: o.ProcedureName,
          forms:
            o.cfgCaseProcedureForms.length !== 0
              ? o.cfgCaseProcedureForms.map<Form>((f) => ({
                  id: f.CaseProcedureFormId,
                  dFormID: (f as any).cfgDForm
                    ? (f as any).cfgDForm.DFormId
                    : '',
                  name: (f as any).cfgDForm
                    ? (f as any).cfgDForm.DFormName
                    : 'No Form Name',
                  filledBy: f.enumFormFilledByID,
                  isOpenByDefault: f.isOpenByDefault,
                  isAddByDefault: f.isAddByDefault,
                  isRequireSignature: f.isRequireSignature,
                  isSubmissionRequired: f.isSubmissionRequired,
                  isToothRequired: f.isToothRequired,
                  formOrder: f.formOrder,
                  FormExpireIn: f.FormExpireIn,
                  enumFormExpireTimeID: f.enumFormExpireTimeID,
                }))
              : [],
          adaCodes: o.cfgCaseProcedureADACodes,
          enumMedicalSpecialtyID: o.enumMedicalSpecialtyID,
          enumProcedureActionCategoryID: o.enumProcedureActionCategoryID,
          enumProcedureCategoryID: o.enumProcedureCategoryID,
          enumProcedureTypeID: o.enumProcedureTypeID,
          deletedForms: [],
          partOfAppointmentGroup: false,
          appointmentAddByDefault: false,
          partOfVisitGroup: false,
          visitAddByDefault: false,
          ParentCaseProcedureId: o.ParentCaseProcedureId,
          order: o.PresentOrder,
          childProcedures: [],
          isExpanded: false,
          isDoNotDeploy: o.isDoNotDeploy,
        }));

        this.selectedProcedure = ret[0];
        this.selectedProcedure.deletedForms = [];
        this.isCreating = false;

        await this.getProcedureGroupInfo(this.selectedProcedure.id);

        await this.proceduresService
          .updateProcedures(this.selectedProcedure.id, this.selectedProcedure)
          .toPromise();
        const og = this.procedures.find(
          (o) => o.id === this.selectedProcedure.id
        );
        if (og) {
          og.name = this.selectedProcedure.name;
          og.code = this.selectedProcedure.code;
          og.description = this.selectedProcedure.description;
          og.partOfAppointmentGroup =
            this.selectedProcedure.partOfAppointmentGroup;
          og.appointmentAddByDefault =
            this.selectedProcedure.appointmentAddByDefault;
          og.partOfVisitGroup = this.selectedProcedure.partOfVisitGroup;
          og.visitAddByDefault = this.selectedProcedure.visitAddByDefault;
          og.forms = this.selectedProcedure.forms;
          og.deletedForms = [];
          og.enumProcedureActionCategoryID =
            this.selectedProcedure.enumProcedureActionCategoryID;
          og.enumMedicalSpecialtyID =
            this.selectedProcedure.enumMedicalSpecialtyID;
          og.enumProcedureTypeID =
            this.selectedProcedure.enumProcedureTypeID;
          og.enumProcedureCategoryID =
            this.selectedProcedure.enumProcedureCategoryID;
          og.ParentCaseProcedureId =
            this.selectedProcedure.ParentCaseProcedureId;
          og.order = this.selectedProcedure.order;
          og.isDoNotDeploy = this.selectedProcedure.isDoNotDeploy;
        }
      } else {
        await this.proceduresService
          .updateProcedures(this.selectedProcedure.id, this.selectedProcedure)
          .toPromise();
        const og = this.procedures.find(
          (o) => o.id === this.selectedProcedure.id
        );
        if (og) {
          og.name = this.selectedProcedure.name;
          og.code = this.selectedProcedure.code;
          og.description = this.selectedProcedure.description;
          og.partOfAppointmentGroup =
            this.selectedProcedure.partOfAppointmentGroup;
          og.appointmentAddByDefault =
            this.selectedProcedure.appointmentAddByDefault;
          og.partOfVisitGroup = this.selectedProcedure.partOfVisitGroup;
          og.visitAddByDefault = this.selectedProcedure.visitAddByDefault;
          og.forms = this.selectedProcedure.forms;
          og.deletedForms = [];
          og.enumProcedureActionCategoryID =
            this.selectedProcedure.enumProcedureActionCategoryID;
          og.enumMedicalSpecialtyID =
            this.selectedProcedure.enumMedicalSpecialtyID;
          og.enumProcedureTypeID =
            this.selectedProcedure.enumProcedureTypeID;
          og.enumProcedureCategoryID =
            this.selectedProcedure.enumProcedureCategoryID;
          og.ParentCaseProcedureId =
            this.selectedProcedure.ParentCaseProcedureId;
          og.order = this.selectedProcedure.order;
          og.isDoNotDeploy = this.selectedProcedure.isDoNotDeploy;
        }
      }

      this.selectedFormID = '';

      this.snackBar.open('Save Successful', 'Saved!', {
        duration: 3000,
        verticalPosition: 'top',
      });
      this.configurationService.setClean();
    } catch (e) {
      this.showErrorMessage(e);
    }
  }

  private showErrorMessage(message: string) {
    this.snackBar.open(message, 'Error', {
      duration: 3000,
      verticalPosition: 'top',
    });
  }

  addForm(): void {
    const dialogRef = this.dialog.open(ProceduresFormComponent, {
      width: '805px',
      height: '725px',
      data: new ProceduresFormData(),
    });

    dialogRef.afterClosed().subscribe((result: ProceduresFormResult) => {
      if (result === null || result === undefined) {
        return;
      }
      this.configurationService.setDirty();

      let maxOrder =
        this.selectedProcedure.forms.length > 0
          ? Math.max(...this.selectedProcedure.forms.map((f) => f.formOrder))
          : 0;

      result.selectedForms.forEach((o) => {
        this.selectedProcedure.forms.push({
          id: this.uuidv4(),
          dFormID: o.DFormId,
          name: o.DFormName,
          isOpenByDefault: false,
          isAddByDefault: false,
          filledBy: '',
          isToothRequired: false,
          formOrder: ++maxOrder,
          FormExpireIn: 0,
          enumFormExpireTimeID: '',
        });
      });

      this.formsTable.updateData(this.selectedProcedure.forms);
      this.save();
    });
  }

  onFormDeleteClick(form: Form) {
    const idx = this.selectedProcedure.forms.findIndex((o) => o.id === form.id);

    this.dialogService
      .openConfirmDialog(`Are you sure you want to delete this item?`)
      .subscribe((result) => {
        if (result === true) {
          if (idx > -1) {
            this.proceduresService.deleteProcedureForm(form.id).subscribe(() => {
              this.selectedProcedure.forms.splice(idx, 1);
              this.formsTable.matDataSource.data = this.selectedProcedure.forms;
              this.formsTable.updateData();
            });
          }
        }
      });
  }

  onProcedureTdoTableItemActionClick(event: TdoTableEvent): void {
    switch (event.action) {
      case TdoTableActionsEnum.DELETE:
        const model = event.value as ProcedureTableViewData;
        const procedureToDelete = model.model;
        const msg = `Are you sure you want to delete ${procedureToDelete.name}?`;

        this.dialogService.openConfirmDialog(msg).subscribe((res) => {
          if (res === true) {
            this.proceduresService
              .deleteProcedure(procedureToDelete.id)
              .subscribe(() => {
                const idx = this.procedures.findIndex(
                  (p) => p.id === procedureToDelete.id
                );
                if (idx > -1) {
                  this.procedures.splice(idx, 1);
                }
              });
          } else {
            // Cancelled by user
          }
        });
        break;
    }
  }

  refreshProcedureFormsDataSource() {
    if (!this.formsTable) {
      return;
    }
    if (!this.selectedProcedure.forms) {
      this.selectedProcedure.forms = [];
    }
    this.formsTable.updateData(this.selectedProcedure.forms);
  }

  changeFormOrder(updatedForm: Form, direction: number) {
    const forms = this.selectedProcedure.forms;

    forms.forEach((f, i) => {
      if (f.formOrder <= 0) {
        f.formOrder = i + 1;
      }
    });

    const index = forms.findIndex((f) => f.id === updatedForm.id);
    if (index === -1) {
      return;
    }

    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= forms.length) {
      return;
    }

    [forms[index], forms[newIndex]] = [forms[newIndex], forms[index]];

    forms.forEach((f, i) => {
      f.formOrder = i + 1;
    });

    this.configurationService.setDirty();
    this.formsTable.updateData(forms);
    this.save();
  }

  onTdoTableRowSelected(event: TdoTableSelectionEvent) {
    const model = event.model;
    this.selectedProcedure = model;
    this.getProcedureGroupInfo(this.selectedProcedure.id);
    this.refreshProcedureFormsDataSource();
  }

  onFormsReordered() {
    const listReordered = this.formsTable.matDataSource.data;
    const listToSave = listReordered.map((p) => p.model as Form);
    let i = 0;
    for (const item of listToSave) {
      item.formOrder = i;
      i++;
    }

    this.save();
  }

  onFilledBySelection(event: EnumValidValueItemModel, row: any) {
    if (row.model.filledBy !== event.ValidValueItemId) {
      this.configurationService.setDirty();
    }

    row.model.filledBy = event.ValidValueItemId;
  }

  deleteProcedure(model: Procedure) {
    let msg = `Are you sure you want to delete ${model.name}?`;
    if (
      model.enumProcedureCategoryID ===
      '00000040-1000-0000-0000-000000000004'
    ) {
      msg +=
        "<br/>This procedure is a repository for patient consent forms.<br/>By deleting it, the system might break. See Practice Locations before proceeding!";
    }

    this.dialogService.openConfirmDialog(msg).subscribe((result) => {
      if (result === true) {
        this.proceduresService.deleteProcedure(model.id).subscribe(() => {
          const idx = this.procedures.findIndex((p) => p.id === model.id);
          if (idx > -1) {
            this.procedures.splice(idx, 1);
            this.procedureTable.updateData(this.procedures);
          } else {
            if (model.ParentCaseProcedureId != null) {
              const parent = this.procedures.find(
                (p) => p.id === model.ParentCaseProcedureId
              );
              if (parent) {
                const childIndex = parent.childProcedures.findIndex(
                  (p) => p.id === model.id
                );
                if (childIndex > -1) {
                  this.procedures.splice(childIndex, 1);
                  this.procedureTable.updateData(this.procedures);
                }
              }
            }
          }
        });
      } else {
        // cancelled
      }
    });
  }

  onProcedureRowSelected(event: TdoTableSelectionEvent) {
    if (this.childProcSelected) {
      this.childProcSelected = false;
      return;
    }

    const dialog = this.configurationService.checkIsDirty();
    if (dialog) {
      dialog.subscribe((res) => {
        if (res) {
          this.changeProcedure(event.row?.model);
          return;
        } else {
          this.procedureTable.selectionControl.select(this.selectedProcedure);
        }
      });
    } else {
      this.changeProcedure(event.row?.model);
    }
  }

  changeProcedure(procedure: Procedure) {
    this.selectedProcedure = null;
    setTimeout(() => {
      this.selectedProcedure = procedure;
      if (!this.selectedProcedure) {
        return;
      }
      this.getProcedureGroupInfo(this.selectedProcedure.id);
      this.refreshProcedureFormsDataSource();
    }, 200);
  }

 drop(event: CdkDragDrop<Procedure[]>, parentProcedure: Procedure) {
    if (!parentProcedure || !parentProcedure.childProcedures) {
      return;
    }
    // Rearrange the childProcedures array based on the event indexes
    moveItemInArray(parentProcedure.childProcedures, event.previousIndex, event.currentIndex);
  
    // Call the service to update the new order in the database
    this.proceduresService
      .changeChildOrder(parentProcedure.childProcedures.map(p => p.id))
      .subscribe(() => {
      });
  }
  

  onChildProcedureSelected(child: Procedure) {
    this.childProcSelected = true;

    setTimeout(() => {
      this.selectedProcedure = child;
      this.refreshProcedureFormsDataSource();
    }, 10);
  }

  convertToAdaCodes() {
    this.proceduresService.convertToAdaCodes().subscribe(() => {
      // implementation
    });
  }

  deleteAdaCode(ada: CaseProcedureAdaCodeConfigurationModel) {
    this.dialogService
      .openConfirmDialog(`Are you sure you want to delete ${ada.Code} ?`)
      .subscribe((res) => {
        if (res) {
          this.proceduresService.deleteAdaCode(ada.CaseProcedureCodeId).subscribe(() => {
            const idx = this.selectedProcedure.adaCodes.findIndex(
              (p) => p.CaseProcedureCodeId.toLowerCase() === ada.CaseProcedureCodeId.toLowerCase()
            );
            if (idx > -1) {
              this.selectedProcedure.adaCodes.splice(idx, 1);
            }
          });
        }
      });
  }

  addAdaCode(id: string, codeInput: string) {
    this.proceduresService.addAdaCode(id, codeInput).subscribe((ada) => {
      this.selectedProcedure.adaCodes.push(ada);
      this.codeInput = '';
    });
  }

  checkProcedureFormsIntegrity() {
    this.proceduresService.checkProcedureFormsIntegrity().subscribe((res) => {
      if (res.IsProcedureForms) {
        const config = new MatDialogConfig();
        config.panelClass = 'dialog';
        config.data = res;
        this.dialog.open(ProcedureFormMismatchDialogComponent, config);
      }
    });
  }
}